import emitter from "@/eventBus";
import { createHead } from "@vueuse/head";
import { createPinia } from "pinia";
import { createApp } from "vue";
import { createVuetify } from "vuetify";
import { fr } from "vuetify/locale";
import { aliases, fa } from "vuetify/iconsets/fa";
import Main from "./pages/Main.vue";
import router from "./router";

import "@fortawesome/fontawesome-free/css/all.css";
import "vuetify/styles";
import "./assets/style/main.scss";
import "./scss/animations.scss";

// Vue.config.productionTip = false;

const vuetify = createVuetify({
  locale: {
    locale: "fr",
    fallback: "fr",
    messages: { fr },
  },
  // Align Vuetify's reactive "mobile" breakpoint with the 768px CSS media queries used across the app.
  display: {
    mobileBreakpoint: 768,
  },
  theme: {
    defaultTheme: "gardenTheme",
    themes: {
      gardenTheme: {
        dark: false,
        colors: {
          // Base
          background: "#F6F3EA", // crème chaude
          surface: "#FFFDF8", // cartes
          "surface-variant": "#EFE9DA", // zones secondaires

          // Primary
          primary: "#5F7D61",
          "primary-darken-1": "#4D6A50",

          // Secondary
          secondary: "#8FA77E",

          // Accent (terracotta)
          accent: "#C9825B",

          // UI states (naturels)
          info: "#7DA2B8",
          success: "#6E8B61",
          warning: "#C59A4A",
          error: "#A85C4D",

          // Text
          "on-background": "#2F3A2F",
          "on-surface": "#2F3A2F",
          "on-surface-variant": "#2F3A2F",
          "on-primary": "#FFFFFF",
          "on-secondary": "#FFFFFF",
          "on-accent": "#FFFFFF",
          "on-success": "#FFFFFF",
          "on-warning": "#FFFFFF",
          "on-error": "#FFFFFF",
          "on-info": "#FFFFFF",

          // Custom
          border: "#DED7C8",
          muted: "#8A928A",
          card: "#FFFDF8",
          // Watering: rain reuses `info` (soft blue); irrigation is a lighter tint of it.
          irrigation: "#B1C7D4",
        },
        variables: {
          "border-color": "#DED7C8",
          "border-opacity": 1,
          "high-emphasis-opacity": 0.92,
          "medium-emphasis-opacity": 0.72,
          "disabled-opacity": 0.38,
        },
      },
    },
  },
  defaults: {
    VCard: {
      rounded: "lg",
    },
    VBtn: {
      rounded: "lg",
      // Vuetify 4 dropped the default uppercase transform; restore it for buttons.
      class: "text-uppercase",
    },
    VTab: {
      class: "text-uppercase",
    },
    VTextField: {
      rounded: "lg",
    },
    VSelect: {
      rounded: "lg",
    },
  },
  icons: {
    defaultSet: "fa",
    aliases,
    sets: {
      fa,
    },
  },
});

const app = createApp(Main);
app.use(createPinia());
app.use(router);
app.use(vuetify);
app.use(createHead());
app.config.globalProperties.emitter = emitter;
app.mount("#app");

// Signal the prerenderer (@prerenderer/webpack-plugin) to snapshot the page as
// soon as the initial route has rendered. Public routes are prerendered as static
// content only — data-heavy sections (e.g. the classification tree) load
// client-side and aren't part of the SEO snapshot, so nothing async is awaited
// here. No-op in a normal browser session (nothing listens to the event).
router.isReady().then(() => {
  document.dispatchEvent(new Event("render-event"));
});

// Patch IE8 and earlier
if (!Date.now) {
  Date.now = () => new Date().getTime();
}
